import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowDownCircle,
  ArrowUpCircle,
  ChevronRight,
  CreditCard,
  History,
  Phone,
  ScanLine,
  Store,
  Users,
} from "lucide-react";

import Navbar from "../components/NavbarM";
import { db } from "../firebase";
import { collection, onSnapshot, query, where } from "firebase/firestore";

import "./Home.css";

function Home() {
  const navigate = useNavigate();

  const storeId = localStorage.getItem("storeId");
  const storeName = localStorage.getItem("storeName");

  const [customersServedToday, setCustomersServedToday] = useState(0);
  const [redeemedToday, setRedeemedToday] = useState(0);
  const [earnedToday, setEarnedToday] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!storeId) {
      alert("Store not logged in. Please sign in again.");
      navigate("/", { replace: true });
      return;
    }

    const transactionsRef = collection(db, "transactions");

    const transactionsQuery = query(
      transactionsRef,
      where("storeId", "==", storeId),
    );

    const unsubscribe = onSnapshot(
      transactionsQuery,
      (snapshot) => {
        const now = new Date();

        const startOfToday = new Date(now);
        startOfToday.setHours(0, 0, 0, 0);

        const endOfToday = new Date(now);
        endOfToday.setHours(23, 59, 59, 999);

        let earned = 0;
        let redeemed = 0;

        const uniqueCustomers = new Set();

        snapshot.docs.forEach((transactionDocument) => {
          const transaction = transactionDocument.data();

          /*
           * New transactions use createdAt.
           * Old transactions use timestamp.
           */
          const timestamp = transaction.createdAt || transaction.timestamp;

          if (!timestamp) return;

          let transactionDate;

          if (typeof timestamp.toDate === "function") {
            transactionDate = timestamp.toDate();
          } else {
            transactionDate = new Date(timestamp);
          }

          if (!transactionDate || Number.isNaN(transactionDate.getTime())) {
            return;
          }

          const isToday =
            transactionDate >= startOfToday && transactionDate <= endOfToday;

          if (!isToday) return;

          /*
           * New transactions use memberId.
           * Old transactions use cardNumber.
           */
          const customerIdentifier =
            transaction.memberId ||
            transaction.cardNumber ||
            transaction.customerId ||
            transaction.phoneNumber;

          if (customerIdentifier) {
            uniqueCustomers.add(customerIdentifier);
          }

          /*
           * New transactions use transactionType.
           * Old transactions use type.
           */
          const transactionType =
            transaction.transactionType || transaction.type;

          /*
           * New transactions use pointsChange.
           * Old transactions use points.
           */
          const pointsAmount = Number(
            transaction.pointsChange ?? transaction.points ?? 0,
          );

          if (transactionType === "earn" || transactionType === "add") {
            earned += Math.abs(pointsAmount);
          }

          if (transactionType === "redeem") {
            redeemed += Math.abs(pointsAmount);
          }
        });

        setCustomersServedToday(uniqueCustomers.size);
        setEarnedToday(earned);
        setRedeemedToday(redeemed);
        setLoading(false);
      },
      (error) => {
        console.error("Dashboard error:", error);

        alert("Could not load dashboard data.");

        setLoading(false);
      },
    );

    return () => unsubscribe();
  }, [storeId, navigate]);

  const today = new Date().toLocaleDateString("en-CA", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });

  return (
    <>
      <Navbar />

      <div className="home-page">
        <div className="home-container">
          <section className="home-hero">
            <div>
              <div className="store-pill">
                <Store size={18} />
                Store Dashboard
              </div>

              <h1>{storeName || "PointsHub Store"}</h1>
              <p>{today}</p>
            </div>

            <div className="hero-status">
              <span className="status-dot"></span>
              Live
            </div>
          </section>

          {loading ? (
            <div className="loading-card">Loading dashboard...</div>
          ) : (
            <section className="stats-grid">
              <div className="stat-card">
                <div className="stat-icon customers">
                  <Users size={26} />
                </div>

                <div>
                  <p>Customers Served</p>
                  <h2>{customersServedToday}</h2>
                  <span>Today</span>
                </div>
              </div>

              <div className="stat-card">
                <div className="stat-icon earned">
                  <ArrowUpCircle size={26} />
                </div>

                <div>
                  <p>Points Earned</p>
                  <h2>{earnedToday.toLocaleString()}</h2>
                  <span>Today</span>
                </div>
              </div>

              <div className="stat-card">
                <div className="stat-icon redeemed">
                  <ArrowDownCircle size={26} />
                </div>

                <div>
                  <p>Points Redeemed</p>
                  <h2>{redeemedToday.toLocaleString()}</h2>
                  <span>Today</span>
                </div>
              </div>
            </section>
          )}

          <section className="quick-actions-section">
            <div className="section-heading">
              <div>
                <p className="section-label">Customer service</p>
                <h2>Quick Actions</h2>
              </div>

              <p className="section-description">
                Find customers, add points and manage store transactions.
              </p>
            </div>

            <div className="quick-actions-grid">
              <button
                type="button"
                className="action-card action-card-primary"
                onClick={() => navigate("/pages/find-customer")}
              >
                <div className="action-icon">
                  <Phone size={27} />
                </div>

                <div className="action-content">
                  <h3>Add Loyalty</h3>

                  <p>
                    Enter the customer's phone number. New numbers are
                    registered automatically.
                  </p>

                  <span>
                    Enter phone number
                    <ChevronRight size={18} />
                  </span>
                </div>
              </button>

              <button
                type="button"
                className="action-card"
                onClick={() => navigate("/pages/transaction-history")}
              >
                <div className="action-icon">
                  <History size={27} />
                </div>

                <div className="action-content">
                  <h3>Transaction History</h3>

                  <p>Review points earned, redeemed and recent activity.</p>

                  <span>
                    View transactions
                    <ChevronRight size={18} />
                  </span>
                </div>
              </button>

              <button
                type="button"
                className="action-card"
                onClick={() => navigate("/pages/scan-customer")}
              >
                <div className="action-icon">
                  <ScanLine size={27} />
                </div>

                <div className="action-content">
                  <h3>Scan Customer Code</h3>

                  <p>
                    Scan the customer’s PointsHub code to earn or redeem
                    rewards.
                  </p>

                  <span>
                    Scan code
                    <ChevronRight size={18} />
                  </span>
                </div>
              </button>
            </div>
          </section>

          <section className="legacy-section">
            <div className="legacy-content">
              <div className="legacy-icon">
                <CreditCard size={24} />
              </div>

              <div>
                <h3>Magnetic card backup</h3>

                <p>
                  Continue using the existing swipe system while the new scanner
                  system is being developed.
                </p>
              </div>
            </div>

            <button
              type="button"
              className="legacy-button"
              onClick={() => navigate("/pages/swipe")}
            >
              Open Swipe Card
              <ChevronRight size={18} />
            </button>
          </section>

          <section className="security-notice">
            <div className="security-notice-icon">
              <ScanLine size={22} />
            </div>

            <div>
              <strong>Redemption security</strong>

              <p>
                Phone-number lookup can only be used to earn points. Customers
                must scan their PointsHub code to redeem rewards.
              </p>
            </div>
          </section>
        </div>
      </div>
    </>
  );
}

export default Home;